import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Volume2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { IconRenderer } from "../lib/icons";
import { speak } from "../lib/speech";
import Header from "../components/Header";
import UserNav from "../components/UserNav";

export default function StoriesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stories, setStories] = useState(null);
  const [story, setStory] = useState(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const pid = localStorage.getItem("rb_profile");
        if (id) {
          const { data } = await api.get(`/stories/${id}`);
          setStory(data);
        } else {
          const { data } = await api.get("/stories", { params: pid ? { profile_id: pid } : {} });
          setStories(data);
        }
      } catch (e) {
        toast.error(apiError(e));
        navigate("/use");
      }
    })();
  }, [id, navigate]);

  const readPage = (text) => speak(text);

  // Reader view
  if (id) {
    if (!story) return <div className="min-h-screen bg-background"><Header /><p className="p-8 text-lg">Loading…</p></div>;
    const p = story.pages[page];
    const last = page === story.pages.length - 1;
    return (
      <div data-testid="story-reader" className="min-h-screen bg-background">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-8 pb-32">
          <button data-testid="back-button" onClick={() => navigate("/use/stories")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
            <ArrowLeft size={20} strokeWidth={2.5} /> All stories
          </button>
          <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-center">{story.title}</h1>
          <p className="mt-1 text-center text-lg font-bold text-muted-foreground">Page {page + 1} of {story.pages.length}</p>

          <div className="mt-8 bg-white rounded-3xl border-2 border-foreground/15 p-8 text-center">
            <span className="mx-auto w-36 h-36 rounded-3xl bg-primary/10 grid place-items-center">
              <IconRenderer name={p.icon} size={88} className="text-primary" />
            </span>
            <p data-testid="story-page-text" className="mt-6 text-3xl font-extrabold leading-snug">{p.text}</p>
            <button
              data-testid="story-read-aloud"
              onClick={() => readPage(p.text)}
              className="mt-6 min-h-[56px] px-6 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 mx-auto hover:bg-secondary transition-colors duration-200"
            >
              <Volume2 size={24} strokeWidth={2.5} /> Read to me
            </button>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              data-testid="story-prev"
              onClick={() => setPage((x) => Math.max(0, x - 1))}
              disabled={page === 0}
              className="min-h-[64px] px-6 rounded-full border-2 border-foreground/25 bg-white font-bold text-lg flex items-center gap-2 disabled:opacity-40"
            >
              <ArrowLeft size={22} strokeWidth={2.5} /> Back
            </button>
            {last ? (
              <button data-testid="story-finish" onClick={() => { speak("The end. Well done!"); navigate("/use/stories"); }} className="flex-1 min-h-[64px] rounded-full bg-[#48BB78] text-white font-extrabold text-xl flex items-center justify-center gap-2">
                <Check size={26} strokeWidth={3} /> The end
              </button>
            ) : (
              <button data-testid="story-next" onClick={() => { const t = story.pages[page + 1].text; setPage(page + 1); speak(t); }} className="flex-1 min-h-[64px] rounded-full bg-primary text-white font-extrabold text-xl flex items-center justify-center gap-2">
                Next <ArrowRight size={22} strokeWidth={3} />
              </button>
            )}
          </div>
        </main>
        <UserNav />
      </div>
    );
  }

  // List view
  return (
    <div data-testid="stories-list" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-8 pb-32">
        <button data-testid="back-button" onClick={() => navigate("/use")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>
        <h1 className="mt-6 text-3xl font-extrabold tracking-tight">My stories</h1>
        <div className="mt-6 space-y-4">
          {stories === null ? (
            <p className="text-lg text-muted-foreground">Loading…</p>
          ) : stories.length === 0 ? (
            <p data-testid="no-stories-message" className="bg-white rounded-3xl border-2 border-dashed border-foreground/20 p-8 text-lg text-muted-foreground">
              No stories yet. Ask your carer to make one — they can create a personalised story about anything new or tricky.
            </p>
          ) : (
            stories.map((s) => (
              <button
                key={s.id}
                data-testid={`open-story-${s.id}`}
                onClick={() => navigate(`/use/stories/${s.id}`)}
                className="w-full bg-white rounded-3xl border-2 border-foreground/10 p-6 flex items-center gap-5 text-left transition-transform duration-200 ease-out hover:-translate-y-px"
              >
                <span className="w-20 h-20 rounded-2xl bg-[#9F7AEA]/10 grid place-items-center shrink-0">
                  <BookOpen size={44} strokeWidth={2.5} className="text-[#9F7AEA]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-2xl font-extrabold truncate">{s.title}</span>
                  <span className="block text-lg text-muted-foreground font-semibold">{s.pages.length} pages</span>
                </span>
                <span className="min-h-[56px] px-6 rounded-full bg-[#9F7AEA] text-white font-bold text-lg flex items-center shrink-0">Read</span>
              </button>
            ))
          )}
        </div>
      </main>
      <UserNav />
    </div>
  );
}
